import { getDirectorySize, IFDEntry, writeDirectory, writeTiffHeader } from "./ifd"
import { DataStream } from "./data-stream"
import { TagCode } from "./tag-codes"
import { TagType } from "./tag-types"
import { getJpegMarkersFromBytes, JpegMarkers, makeSegmentHeader, Metadata } from "@/core/exif/jpeg"
import { makeXMP } from "./xmp"

/**
 * Encode/decode Exif APEX values
 */

export const decodeShutterSpeedValue = (value: number) => {
	return Math.pow(2, -value)
}

export const encodeShutterSpeedValue = (value: number) => {
	return -1 * Math.log2(value)
}

export const decodeApertureValue = (value: number) => {
	return Math.pow(2, value / 2)
}

export const encodeApertureValue = (value: number) => {
	return 2 * Math.log2(value)
}

/**
 * Write Exif
 */

function makeExif(metadata: Metadata) {
	const TiffIFD = metadata.TIFF
	const ExifIFD = metadata.Exif

	const ExifPointerEntry: IFDEntry = { tag: TagCode.ExifIFD, type: TagType.LONG, value: 0 }

	TiffIFD.push(ExifPointerEntry)

	const TiffIFDSize = getDirectorySize(TiffIFD)
	const ExifIFDSize = getDirectorySize(ExifIFD)

	const tiffHeaderSize = 8
	const sectionSize = TiffIFDSize + ExifIFDSize + tiffHeaderSize

	ExifPointerEntry.value = TiffIFDSize + tiffHeaderSize

	const buffer = new ArrayBuffer(sectionSize)
	const dataStream = new DataStream(buffer)

	writeTiffHeader(dataStream)
	writeDirectory(dataStream, TiffIFD, dataStream.offset)
	writeDirectory(dataStream, ExifIFD, dataStream.offset)

	return buffer
}

const concatBytes = (parts: Uint8Array[]) => {
	const byteLength = parts.reduce((sum, part) => sum + part.byteLength, 0)
	const output = new Uint8Array(byteLength)
	let offset = 0

	for (const part of parts) {
		output.set(part, offset)
		offset += part.byteLength
	}

	return output
}

const bufferToBytes = (buffer: ArrayBuffer) => new Uint8Array(buffer)

export function writeJpegExifToBytes(jpegData: Uint8Array, metadata: Metadata) {
	const markers = getJpegMarkersFromBytes(jpegData)

	const APP0 = markers["APP0"] // JFIF
	const APP1 = markers["APP1"] // Exif
	const APP2 = markers["APP2"] // ICC

	// Splice APP1 section into JPEG bytes
	// Replace APP2 section with custom ICC profile if needed
	const spliceStart = APP0?.start ?? APP1?.start ?? 2
	const spliceEnd = APP2?.end ?? APP1?.end ?? APP0?.end ?? 2

	const parts: Uint8Array[] = [jpegData.subarray(0, spliceStart)]

	const exif = makeExif(metadata)
	const exifSegmentHeader = makeSegmentHeader(JpegMarkers.APP1, "Exif", exif.byteLength)
	parts.push(bufferToBytes(exifSegmentHeader), bufferToBytes(exif))

	const xmp = makeXMP(metadata.XMP)
	const xmpSegmentHeader = makeSegmentHeader(
		JpegMarkers.APP1,
		"http://ns.adobe.com/xap/1.0/",
		xmp.byteLength,
	)
	parts.push(bufferToBytes(xmpSegmentHeader), bufferToBytes(xmp))

	if (metadata.ICCProfile) {
		const iccSequenceId = new Uint8Array([1, 1])
		const iccSegmentHeader = makeSegmentHeader(
			JpegMarkers.APP2,
			"ICC_PROFILE",
			metadata.ICCProfile.byteLength + iccSequenceId.byteLength,
		)
		parts.push(bufferToBytes(iccSegmentHeader), iccSequenceId, metadata.ICCProfile)
	}

	parts.push(jpegData.subarray(spliceEnd))

	return concatBytes(parts)
}

export async function writeJpegExif(blob: Blob, metadata: Metadata) {
	return new Blob([writeJpegExifToBytes(new Uint8Array(await blob.arrayBuffer()), metadata)], {
		type: blob.type,
	})
}
